"use client";

import { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { ChevronLeft, CreditScoreOutlined, PaymentsOutlined } from "@mui/icons-material";
import { SVariantDto } from "@/__generated__/sdk";
import { effectiveSellerPrice, filterSellerOffers, getSellerOffersForVariants, SellerOfferFilter } from "../sellerOffers";
import ProductBottomSheet from "./ProductBottomSheet";
import { SellerOfferRows } from "./VariantSellerOffers";

type ModeKey = "cash" | "credit";

type Props = {
  open: boolean;
  onClose: () => void;
  variants: SVariantDto[];
  productId: string;
  /** "all" → mode picker when both exist; "cash"/"credit" → open that list (back still returns to picker). */
  initialFilter: SellerOfferFilter;
};

const MODE_META: Record<
  ModeKey,
  { listTitle: string; optionTitle: string; optionHint: string; Icon: typeof PaymentsOutlined; color: string; tint: string }
> = {
  cash: {
    listTitle: "فروشندگان نقدی",
    optionTitle: "پرداخت نقدی",
    optionHint: "همین الان از حساب یا کارت",
    Icon: PaymentsOutlined,
    color: "#D48136",
    tint: "rgba(212, 129, 54, 0.12)",
  },
  credit: {
    listTitle: "فروشندگان اعتباری",
    optionTitle: "پرداخت اعتباری",
    optionHint: "با اعتبار طرف‌قراردادها",
    Icon: CreditScoreOutlined,
    color: "#2F80ED",
    tint: "rgba(47, 128, 237, 0.1)",
  },
};

function ModeRow({
  mode,
  count,
  fromPrice,
  onClick,
}: {
  mode: ModeKey;
  count: number;
  fromPrice: number | null;
  onClick: () => void;
}) {
  const meta = MODE_META[mode];
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "start",
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        px: 1.25,
        py: 1.1,
        "&:active": { bgcolor: "grey.50" },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.1}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: meta.tint,
            color: meta.color,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <meta.Icon sx={{ fontSize: 20 }} />
        </Box>
        <Stack spacing={0.15} flex={1} minWidth={0}>
          <Typography fontWeight={800} sx={{ fontSize: "0.875rem", lineHeight: 1.3 }}>
            {meta.optionTitle}
          </Typography>
          <Typography variant="caption" color="grey.600" noWrap sx={{ fontSize: "0.72rem", lineHeight: 1.35 }}>
            {meta.optionHint}
            {" · "}
            {count.toLocaleString("fa-IR")} فروشنده
          </Typography>
        </Stack>
        <Stack alignItems="flex-end" spacing={0.1} sx={{ flexShrink: 0 }}>
          {fromPrice != null ? (
            <Typography fontWeight={800} sx={{ fontSize: "0.78rem", color: "grey.800", lineHeight: 1.2 }}>
              از {fromPrice.toLocaleString("fa-IR")}
            </Typography>
          ) : null}
          <Typography variant="caption" color="grey.500" sx={{ fontSize: "0.65rem" }}>
            تومان
          </Typography>
        </Stack>
        <ChevronLeft sx={{ color: "grey.400", fontSize: 20, flexShrink: 0, mr: -0.25 }} />
      </Stack>
    </ButtonBase>
  );
}

export default function ProductSellersSheet({ open, onClose, variants, productId, initialFilter }: Props) {
  const allOffers = useMemo(() => getSellerOffersForVariants(variants), [variants]);
  const cashOffers = useMemo(() => filterSellerOffers(allOffers, "cash"), [allOffers]);
  const creditOffers = useMemo(() => filterSellerOffers(allOffers, "credit"), [allOffers]);
  const bothModes = cashOffers.length > 0 && creditOffers.length > 0;

  const [mode, setMode] = useState<ModeKey | null>(null);

  useEffect(() => {
    if (!open) {
      setMode(null);
      return;
    }
    if (initialFilter === "credit" && creditOffers.length) {
      setMode("credit");
      return;
    }
    if (initialFilter === "cash" && cashOffers.length) {
      setMode("cash");
      return;
    }
    if (!bothModes) {
      setMode(cashOffers.length ? "cash" : creditOffers.length ? "credit" : null);
      return;
    }
    setMode(null);
  }, [open, initialFilter, bothModes, cashOffers.length, creditOffers.length]);

  const listOffers = mode === "credit" ? creditOffers : mode === "cash" ? cashOffers : [];
  const showPicker = open && mode == null && bothModes;
  const canBack = Boolean(mode && bothModes);

  const title = showPicker
    ? "نقدی بخرید یا اعتباری؟"
    : mode
      ? MODE_META[mode].listTitle
      : "فروشندگان این کالا";

  const subtitle = showPicker
    ? "قیمت و فروشنده برای هر روش پرداخت فرق دارد؛ اول روش را مشخص کنید."
    : listOffers.length
      ? `${listOffers.length.toLocaleString("fa-IR")} فروشنده · مرتب‌شده از ارزان‌ترین`
      : undefined;

  return (
    <ProductBottomSheet
      open={open}
      onClose={onClose}
      onBack={canBack ? () => setMode(null) : undefined}
      title={title}
      subtitle={subtitle}
    >
      {showPicker ? (
        <Stack spacing={1} sx={{ pt: 0.25 }}>
          {cashOffers.length ? (
            <ModeRow
              mode="cash"
              count={cashOffers.length}
              fromPrice={effectiveSellerPrice(cashOffers[0])}
              onClick={() => setMode("cash")}
            />
          ) : null}
          {creditOffers.length ? (
            <ModeRow
              mode="credit"
              count={creditOffers.length}
              fromPrice={effectiveSellerPrice(creditOffers[0])}
              onClick={() => setMode("credit")}
            />
          ) : null}
        </Stack>
      ) : (
        <SellerOfferRows offers={listOffers} productId={productId} compact />
      )}
    </ProductBottomSheet>
  );
}
